import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LoginRequest } from '../models/auth/login-request.model';
import { LoginResponse } from '../models/auth/login-response.model';
import { AnonymousResponseModel } from '../models/auth/anonymous-response.model';
import { catchError, Observable, of, tap } from 'rxjs';
import { RefreshResponse } from '../models/auth/refresh-response.model';
import { InternalUserLoginResponse } from '../models/auth/internal-user-login-response.model';
import { RegisterConfirmRequest } from '../models/auth/register-confirm-request.model';
import { RegisterRequest } from '../models/auth/register-request.model';
import { ForgotPasswordInitRequest } from '../models/auth/forgot-password-init-request.model';
import { ForgotPasswordProcessRequest } from '../models/auth/forgot-password-process-request.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private accessToken: string | null = null;

  private readonly anonymousFlagKey = 'fran-anonymous';
  private readonly _isAnonymous = signal<boolean>(
    localStorage.getItem(this.anonymousFlagKey) === 'true',
  );
  readonly isAnonymous = this._isAnonymous.asReadonly();

  private readonly internalUserFlagKey = 'fran-internal';
  private readonly internalCrmTokenKey = 'fran-internal-crm-token';
  private readonly internalObjectIdKey = 'fran-internal-object-id';
  private readonly _isInternalUser = signal<boolean>(
    localStorage.getItem(this.internalUserFlagKey) === 'true',
  );
  readonly isInternalUser = this._isInternalUser.asReadonly();

  isAuthenticated = signal<boolean>(false);

  setToken(token: string) {
    this.accessToken = token;
    this.isAuthenticated.set(true);
  }

  getToken(): string | null {
    return this.accessToken;
  }

  clear(): void {
    this.accessToken = null;
    this.isAuthenticated.set(false);
    this._isAnonymous.set(false);
    localStorage.removeItem(this.anonymousFlagKey);
    this._isInternalUser.set(false);
    localStorage.removeItem(this.internalUserFlagKey);
    localStorage.removeItem(this.internalCrmTokenKey);
    localStorage.removeItem(this.internalObjectIdKey);
  }

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}auth/login`, payload, {
        withCredentials: true,
      })
      .pipe(
        tap((response: LoginResponse) => {
          this.setToken(response.accessToken);
          this._isAnonymous.set(false);
          localStorage.removeItem(this.anonymousFlagKey);
          this._isInternalUser.set(false);
          localStorage.removeItem(this.internalUserFlagKey);
          localStorage.removeItem(this.internalCrmTokenKey);
          localStorage.removeItem(this.internalObjectIdKey);
        }),
      );
  }

  loginInternalUser(crmToken: string, objectId: string): Observable<InternalUserLoginResponse> {
    return this.http
      .post<InternalUserLoginResponse>(
        `${environment.apiUrl}auth/internal-login/${crmToken}/objects/${objectId}`,
        {},
        { withCredentials: true },
      )
      .pipe(
        tap((response: InternalUserLoginResponse) => {
          this.setToken(response.accessToken);
          this._isAnonymous.set(false);
          localStorage.removeItem(this.anonymousFlagKey);
          this._isInternalUser.set(true);
          localStorage.setItem(this.internalUserFlagKey, 'true');
          localStorage.setItem(this.internalCrmTokenKey, crmToken);
          localStorage.setItem(this.internalObjectIdKey, objectId);
        }),
      );
  }

  refresh(): Observable<RefreshResponse> {
    if (this._isInternalUser()) {
      const crmToken = localStorage.getItem(this.internalCrmTokenKey);
      const objectId = localStorage.getItem(this.internalObjectIdKey);

      if (crmToken && objectId) {
        return this.loginInternalUser(crmToken, objectId);
      }
    }

    return this.http
      .post<RefreshResponse>(
        `${environment.apiUrl}auth/refresh`,
        {},
        {
          withCredentials: true,
        },
      )
      .pipe(tap((response: RefreshResponse) => this.setToken(response.accessToken)));
  }

  logout() {
    return this.http.post(
      `${environment.apiUrl}auth/logout`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  initAuth(): Observable<RefreshResponse | null> {
    return this.refresh().pipe(
      catchError(() => {
        this.clear();
        return of(null);
      }),
    );
  }

  register(payload: RegisterRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}auth/register`, payload);
  }

  registerConfirm(payload: RegisterConfirmRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}auth/email/verify`, payload);
  }

  forgotPasswordInit(payload: ForgotPasswordInitRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}auth/password/forgot`, payload);
  }

  forgotPasswordProcess(payload: ForgotPasswordProcessRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}auth/password/reset`, payload);
  }

  anonymous(): Observable<AnonymousResponseModel> {
    return this.http
      .post<AnonymousResponseModel>(
        `${environment.apiUrl}auth/anonymous`,
        {},
        { withCredentials: true },
      )
      .pipe(
        tap((response: AnonymousResponseModel) => {
          this.setToken(response.accessToken);
          this._isAnonymous.set(true);
          localStorage.setItem(this.anonymousFlagKey, 'true');
          this._isInternalUser.set(false);
          localStorage.removeItem(this.internalUserFlagKey);
          localStorage.removeItem(this.internalCrmTokenKey);
          localStorage.removeItem(this.internalObjectIdKey);
        }),
      );
  }
}
